import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { AddOnDetail } from '../classes/add-on-detail';
import { AppConfiguration } from '../classes/configuration';
import { DriverTask } from '../classes/driver-task';
import { DropOffTask } from '../classes/dropoff-task';
import { OutboundAssignTask } from '../classes/outbound-assign-tasks';
import { Passenger } from '../classes/passenger';
import { Profile } from '../classes/profile';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { ProfileService } from './profile.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class OutboundService implements OnDestroy {
  configuration: AppConfiguration;
  taskUrl: string;
  private destroy$ = new Subject<void>();

  constructor(
    readonly profileService: ProfileService,
    readonly configurationService: ConfigurationService,
    readonly http: HttpClient,
    private logger: LoggerService,
    private toastService: ToastService
  ) {
    this.subscribeToConfiguration();
  }

  subscribeToConfiguration(): void {
    this.configurationService.getConfiguration().pipe(takeUntil(this.destroy$)).subscribe((configuration) => {
      if (configuration) {
        this.configuration = configuration;
        this.taskUrl = configuration.HostApi.concat('/task');
      }
    });
  }

  assignPickupOutboundTasks(tasks: DriverTask[]): Observable<any> {
    return this.profileService.getProfileObs().pipe(
      map((profile: Profile) =>
        tasks.map((task) => {
          const assignTask = new OutboundAssignTask();
          assignTask.taskIds = [ task.taskName ];
          assignTask.assignTo = profile.adId;
          // Only assign passengers and addons that were selected
          assignTask.passengers = task.passengers
            .filter((passenger: Passenger) => passenger.selected)
            .map((passenger: Passenger) => `${passenger.id}`);
          assignTask.addOnDetails = task.addOnDetails
            .filter((addOnDetail: AddOnDetail) => addOnDetail.selected)
            .map((addOnDetail: AddOnDetail) => ({
              id: addOnDetail.id,
              name: addOnDetail.name,
              type: addOnDetail.type
            }));
          assignTask.driverNotes = task.driverNotes;
          return assignTask;
        })
      ),
      switchMap((assignedTasks: OutboundAssignTask[]) => this.http.put(this.taskUrl, assignedTasks)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to assign outbound tasks'))
    );
  }

  dropOffTask(task: DriverTask): Observable<any> {
    return this.profileService.getProfileObs().pipe(
      map((profile: Profile) => ({
        ...new DropOffTask(),
        taskIds: [ task.taskName ],
        assignTo: profile.adId,
        // Only drop off passengers and addons that were selected
        passengers: (task.passengers || [])
          .filter((passenger: Passenger) => passenger.selected)
          .map((passenger: Passenger) => `${passenger.id}`),
        addOnDetails: (task.addOnDetails || [])
          .filter((addOnDetail: AddOnDetail) => addOnDetail.selected)
          .map((addOnDetail: AddOnDetail) => ({
            id: addOnDetail.id,
            name: addOnDetail.name,
            type: addOnDetail.type
          })),
        driverNotes: task.driverNotes
      })),
      switchMap((dropOffTask: DropOffTask) => this.http.put(`${this.taskUrl}/${task.taskName}`, dropOffTask)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to drop off outbound task'))
    );
  }

  private handleError(error: HttpErrorResponse, message: string) {
    this.toastService.show('HTTP Error Occurred!', { classname: 'bg-danger text-light', delay: 8000 });
    if (error.status === 0) {
      this.logger.error(`${message}:`, error.error);
    } else {
      this.logger.error(`${message}: ${error.status}, body was: ${error.error}`);
    }
    return throwError(error);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
